import {
  customerName,
  customerPhone,
  formatDisplayNumber,
  formatEnum,
  formatOrder,
} from './promotion-report-format';

const escapeCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  if (!/[",\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
};

const money = (value) =>
  Number.isFinite(value) ? value.toFixed(2) : value ?? '';

const section = (title, headers, rows) => {
  if (!rows.length) return [];
  return [
    [title],
    headers,
    ...rows,
    [], // blank line so the sections stay readable in a spreadsheet
  ];
};

/**
 * One file rather than a download per table: the sections are different views
 * of the same period and are only useful side by side.
 */
export function buildPromotionReportCsv(report) {
  const lines = [
    ...section(
      'Summary',
      ['Metric', 'Value'],
      [
        ['Total Discount', money(report.summary?.totalDiscount)],
        ['Promotions Applied', report.summary?.totalUsages ?? 0],
        ['Orders Discounted', report.summary?.uniqueOrderCount ?? 0],
        ['Customers Reached', report.summary?.uniqueCustomerCount ?? 0],
        ['Avg Discount / Usage', money(report.summary?.avgDiscountPerUsage)],
        ['Avg Discount / Order', money(report.summary?.avgDiscountPerOrder)],
      ]
    ),

    ...section(
      'By Promotion',
      ['Promotion', 'Type', 'Usages', 'Orders', 'Discount', 'Avg Discount'],
      (report.byPromotion || []).map((row) => [
        row.promotionName,
        formatEnum(row.type),
        row.usageCount,
        row.uniqueOrderCount,
        money(row.totalDiscount),
        money(row.avgDiscount),
      ])
    ),

    ...section(
      'By Channel',
      ['Channel', 'Usages', 'Orders', 'Discount'],
      (report.byChannel || []).map((row) => [
        formatEnum(row.channel),
        row.usageCount,
        row.uniqueOrderCount,
        money(row.totalDiscount),
      ])
    ),

    ...section(
      'By Type',
      ['Type', 'Usages', 'Discount'],
      (report.byType || []).map((row) => [
        formatEnum(row.type),
        row.usageCount,
        money(row.totalDiscount),
      ])
    ),

    ...section(
      'By Status',
      ['Status', 'Usages', 'Discount'],
      (report.byStatus || []).map((row) => [
        formatEnum(row.status),
        row.usageCount,
        money(row.totalDiscount),
      ])
    ),

    ...section(
      'Daily Trend',
      ['Date', 'Usages', 'Discount'],
      (report.dailyTrend || []).map((row) => [
        row.date,
        row.usageCount,
        money(row.totalDiscount),
      ])
    ),

    ...section(
      'Top Customers',
      ['Customer', 'Phone', 'Usages', 'Discount'],
      (report.topCustomers || []).map((row) => [
        customerName(row),
        customerPhone(row),
        row.usageCount,
        money(row.totalDiscount),
      ])
    ),

    ...section(
      'Coupons',
      ['Code', 'Redemptions', 'Discount', 'Customers', 'Cancelled'],
      (report.coupons?.items || []).map((row) => [
        row.code,
        row.redemptionCount,
        money(row.totalDiscount),
        row.uniqueCustomerCount,
        row.cancelledCount,
      ])
    ),

    // Only the rows already pulled in: exporting the rest would mean paging the
    // whole history behind the user's back.
    ...section(
      'Usage Detail (loaded rows)',
      [
        'Date',
        'Order',
        'Display #',
        'Promotion',
        'Type',
        'Channel',
        'Customer',
        'Phone',
        'Status',
        'Discount',
      ],
      (report.detailItems || []).map((row) => [
        row.createdAt,
        formatOrder(row),
        formatDisplayNumber(row),
        row.promotionName,
        formatEnum(row.type),
        formatEnum(row.channel),
        customerName(row),
        customerPhone(row),
        formatEnum(row.status),
        money(row.discountAmount),
      ])
    ),
  ];

  return lines.map((cells) => cells.map(escapeCell).join(',')).join('\n');
}

export function exportPromotionReportCsv(report) {
  const csv = buildPromotionReportCsv(report);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  const start = report.filters?.startDate || '';
  const end = report.filters?.endDate || '';
  const range =
    start && end ? `${start}_to_${end}` : new Date().toISOString().slice(0, 10);

  link.setAttribute('href', url);
  link.setAttribute('download', `promotion-report-${range}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
